import { useCallback, useEffect, useMemo } from 'react';
import type { VoidFunctionComponent } from 'react';

import { useDialogContext } from '../../../../contexts/DialogContext';
import { useDresser } from '../../../../contexts/DrawerContext';
import useDeleteOIDCProvider from '../../../../hooks/api/oidc/useDeleteOIDCProvider';
import useOIDCProviders from '../../../../hooks/api/oidc/useOIDCProviders';
import { ErrorAlert } from '../../../generic';
import FallbackImage from '../../../generic/FallbackImage';
import Loader from '../../../generic/Loader';
import type { Filter } from '../../../../hooks/api/oidc/useOIDCProviders';

import OIDCProviderDetailsDrawer from './OIDCProviderDetailsDrawer';
import OIDCProviderTable from './OIDCProviderTable';

import styles from './OIDCProviders.module.less';

type OIDCProvidersTableProps = {
  currentTeamId?: number;
  filter?: Filter;
  searchValue: string;
};

const OIDCProvidersTable: VoidFunctionComponent<OIDCProvidersTableProps> = ({
  currentTeamId,
  filter,
  searchValue,
}) => {
  // Data fetching
  const {
    data: pages,
    isLoading,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useOIDCProviders(filter);

  const deleteProviderMutation = useDeleteOIDCProvider();
  const { onConfirm } = useDialogContext();
  const { pushDrawer, clearDrawers } = useDresser();

  // Auto-pagination
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && !isLoading) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, isLoading, fetchNextPage]);

  // Flatten pages
  const allProviders = useMemo(
    () => pages?.pages.flatMap((page) => page.results) ?? [],
    [pages],
  );

  // Search filtering
  const matchingProviders = useMemo(
    () =>
      allProviders.filter((provider) =>
        provider.issuer.toLowerCase().includes(searchValue.toLowerCase()),
      ),
    [allProviders, searchValue],
  );

  // Delete handler
  const onDeleteProvider = useCallback(
    (id: number) => {
      onConfirm({
        title: 'Delete OIDC Provider',
        body: 'Are you sure you want to delete this OIDC provider?',
        onConfirm: async () => {
          await deleteProviderMutation.mutateAsync(id);
        },
      });
    },
    [onConfirm, deleteProviderMutation],
  );

  // View handler
  const onViewProvider = useCallback(
    (id: number) => {
      const provider = allProviders.find((p) => p.id === id);
      if (provider) {
        pushDrawer(<OIDCProviderDetailsDrawer provider={provider} currentTeamId={currentTeamId} />, {
          shouldSkipClickOutside: true,
          onClose: clearDrawers,
        });
      }
    },
    [allProviders, currentTeamId, pushDrawer, clearDrawers],
  );

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <ErrorAlert status={{ errors: [error.data] }} />
      </div>
    );
  }

  if (isLoading || isFetchingNextPage) {
    return <Loader />;
  }

  if (allProviders.length === 0) {
    return (
      <div className={styles.emptyStateContainer}>
        <FallbackImage src="no-results" alt="No OIDC providers">
          <div className={styles.emptyStateText}>There aren&apos;t any OIDC providers.</div>
        </FallbackImage>
      </div>
    );
  }

  return (
    <div className={styles.tableContainer}>
      <OIDCProviderTable
        providers={matchingProviders}
        onDeleteProvider={onDeleteProvider}
        onViewProvider={onViewProvider}
        isLoading={deleteProviderMutation.isPending}
        shouldShowScope={!filter}
      />
    </div>
  );
};

export default OIDCProvidersTable;
